import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PiWarningCircleLight, PiCheckCircleLight, PiColumnsLight, PiCurrencyDollarLight, PiDownloadSimpleLight, PiEyeLight, PiSignatureLight, PiFunnelLight, PiCircleNotchLight, PiEnvelopeSimpleLight, PiChatCircleLight, PiPlusLight, PiMagnifyingGlassLight, PiShieldCheckLight, PiSlidersHorizontalLight, PiArrowUUpLeftLight, PiXLight, PiXCircleLight } from 'react-icons/pi';
import { offerAPI } from '../../services/api';
import { API_BASE_URL } from '../../services/apiService';
import SkeletonBlock from '../../components/common/SkeletonBlock';
import { USD_CURRENCY } from '../../utils/compensation';

const OPEN_STATUSES = ['sent', 'viewed', 'negotiation_pending', 'negotiation_countered'];
const NEGOTIATION_STATUSES = ['negotiation_pending', 'negotiation_countered'];

const statusMeta = {
  sent: { label: 'Sent', tone: 'border-sky-100 bg-sky-50 text-sky-700', dot: 'bg-sky-500' },
  viewed: { label: 'Viewed', tone: 'border-cyan-100 bg-cyan-50 text-cyan-700', dot: 'bg-cyan-500' },
  negotiation_pending: { label: 'Negotiation', tone: 'border-amber-100 text-amber-600', dot: 'bg-amber-500' },
  negotiation_countered: { label: 'Countered', tone: 'border-violet-100 bg-violet-50 text-violet-700', dot: 'bg-violet-500' },
  accepted: { label: 'Accepted', tone: 'border-emerald-100 text-emerald-600', dot: 'bg-emerald-500' },
  rejected: { label: 'Declined', tone: 'border-rose-100 bg-rose-50 text-rose-700', dot: 'bg-rose-500' },
  withdrawn: { label: 'Withdrawn', tone: 'border-slate-200 bg-slate-50 text-slate-600', dot: 'bg-slate-400' },
  expired: { label: 'Expired', tone: 'border-orange-100 bg-orange-50 text-orange-700', dot: 'bg-orange-500' }
};

const statusOptions = [
  { value: 'all', label: 'All' },
  { value: 'sent', label: 'Sent' },
  { value: 'viewed', label: 'Viewed' },
  { value: 'negotiation_pending', label: 'Negotiation' },
  { value: 'negotiation_countered', label: 'Countered' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Declined' },
  { value: 'withdrawn', label: 'Withdrawn' },
  { value: 'expired', label: 'Expired' }
];

const salaryPeriods = {
  hourly: 'hour',
  daily: 'day',
  weekly: 'week',
  monthly: 'month',
  yearly: 'year',
  one_time: 'one time'
};

const extractError = (error, fallback) => (
  error?.response?.error ||
  error?.response?.message ||
  error?.message ||
  error?.error ||
  fallback
);

const labelize = (value) => (
  String(value || 'Not set')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(Number(value) || 0);

const formatCurrency = (amount, currency = 'USD') => {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric) || numeric <= 0) return 'Not set';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: String(currency || 'USD').toUpperCase(),
    maximumFractionDigits: 0
  }).format(numeric);
};

const formatSalary = (offer) => {
  const amount = formatCurrency(offer?.salaryAmount, offer?.salaryCurrency);
  const period = salaryPeriods[offer?.salaryPeriod] || labelize(offer?.salaryPeriod).toLowerCase();
  return `${amount}${period ? ` / ${period}` : ''}`;
};

const formatDate = (value, fallback = 'Not set') => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

const formatDateTime = (value, fallback = 'Not recorded') => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
};

const daysUntil = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return Math.ceil((date.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
};

const candidateName = (offer) => {
  const candidate = offer?.candidate || {};
  return [candidate.firstName, candidate.lastName].filter(Boolean).join(' ') || 'Candidate';
};

const initialsFor = (name) => (
  String(name || 'Candidate')
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
);

const parsePayload = (value, fallback = null) => {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch (_) {
    return value;
  }
};

const formatPayload = (value) => {
  const parsed = parsePayload(value, '');
  if (!parsed) return 'Not provided';
  if (typeof parsed === 'string') return parsed;
  return Object.entries(parsed)
    .filter(([, item]) => item !== null && item !== undefined && item !== '')
    .map(([key, item]) => `${labelize(key)}: ${Array.isArray(item) ? item.join(', ') : String(item)}`)
    .join('\n') || 'Not provided';
};

const resolveUploadUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;

  const clean = String(path).replace(/^\/?uploads\/?/, '');
  try {
    return `${new URL(API_BASE_URL).origin}/uploads/${clean}`;
  } catch (_) {
    return `/uploads/${clean}`;
  }
};

const getDocuments = (offer) => {
  const docs = offer?.documents || {};
  const rows = [];

  if (docs.offerLetterPath || docs.offerLetterMetadata?.path) {
    rows.push({
      label: 'Offer letter',
      path: docs.offerLetterPath || docs.offerLetterMetadata?.path,
      metadata: docs.offerLetterMetadata || {}
    });
  }
  if (docs.contractDocumentPath || docs.contractDocumentMetadata?.path) {
    rows.push({
      label: 'Contract',
      path: docs.contractDocumentPath || docs.contractDocumentMetadata?.path,
      metadata: docs.contractDocumentMetadata || {}
    });
  }
  (Array.isArray(docs.additionalDocuments) ? docs.additionalDocuments : []).forEach((document, index) => {
    rows.push({
      label: `Additional document ${index + 1}`,
      path: document?.path,
      metadata: document || {}
    });
  });
  if (docs.signedOfferLetterPath || docs.signedOfferLetterMetadata?.path) {
    rows.push({
      label: 'Signed offer letter',
      path: docs.signedOfferLetterPath || docs.signedOfferLetterMetadata?.path,
      metadata: docs.signedOfferLetterMetadata || {}
    });
  }

  return rows;
};

const latestPendingNegotiation = (negotiations = []) => {
  if (!Array.isArray(negotiations)) return null;
  return negotiations
    .filter((item) => item?.status === 'pending_employer_review')
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0] || null;
};

const StatusPill = ({ status }) => {
  const meta = statusMeta[status] || { label: labelize(status), tone: 'border-slate-200 bg-slate-50 text-slate-600', dot: 'bg-slate-400' };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium ${meta.tone}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
};

const OffersOverviewSkeleton = () => (
  <div className="min-h-screen bg-white px-4 py-6 font-sans sm:px-6 lg:px-8">
    <div className="mx-auto max-w-[1600px] space-y-6">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="space-y-3">
          <SkeletonBlock className="h-7 w-28 rounded-md" />
          <SkeletonBlock className="h-9 w-56 rounded-md" />
          <SkeletonBlock className="h-4 w-[30rem] max-w-full rounded-md" />
        </div>
        <div className="flex flex-wrap gap-2">
          <SkeletonBlock className="h-10 w-28 rounded-lg" />
          <SkeletonBlock className="h-10 w-32 rounded-lg" />
        </div>
      </header>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="rounded-lg border border-slate-200/80 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
            <div className="mb-4 flex items-center justify-between gap-3">
              <SkeletonBlock className="h-9 w-9 rounded-lg" />
              <SkeletonBlock className="h-3 w-20 rounded-md" />
            </div>
            <SkeletonBlock className="h-8 w-24 rounded-md" />
            <SkeletonBlock className="mt-2 h-4 w-28 rounded-md" />
          </div>
        ))}
      </section>

      <section className="rounded-lg border border-slate-200/80 bg-white p-3 shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <SkeletonBlock className="h-10 flex-1 rounded-lg" />
          <SkeletonBlock className="h-10 w-24 rounded-lg lg:hidden" />
          <div className="grid gap-2 sm:grid-cols-2 lg:flex">
            <SkeletonBlock className="h-10 w-full rounded-lg sm:w-32" />
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white px-4 py-2 shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="grid min-w-[1120px] grid-cols-[1.7fr_1.2fr_1fr_1fr_1.1fr_1.2fr] gap-4 border-b border-slate-100 py-4 last:border-0">
            <div className="space-y-2">
              <SkeletonBlock className="h-4 w-40 rounded-md" />
              <SkeletonBlock className="h-3 w-52 rounded-md" />
            </div>
            <SkeletonBlock className="h-4 w-28 rounded-md" />
            <SkeletonBlock className="h-7 w-24 rounded-md" />
            <SkeletonBlock className="h-4 w-24 rounded-md" />
            <SkeletonBlock className="h-4 w-20 rounded-md" />
            <div className="flex justify-end gap-1.5">
              {Array.from({ length: 3 }).map((__, buttonIndex) => <SkeletonBlock key={buttonIndex} className="h-8 w-8 rounded-md" />)}
            </div>
          </div>
        ))}
      </section>
    </div>
  </div>
);

const Metric = ({ icon: Icon, label, value, detail, tone = 'sky' }) => {
  const tones = {
    sky: 'text-sky-600',
    slate: 'text-slate-800',
    green: 'text-emerald-600',
    amber: 'text-amber-600'
  };

  return (
    <div className="rounded-lg border border-slate-200/80 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
      <div className="mb-4 flex items-center justify-between gap-3">
        <span className={`flex items-center justify-center ${tones[tone] || tones.sky}`}>
          <Icon size={24} />
        </span>
        <span className="text-xs font-medium text-slate-400">{detail}</span>
      </div>
      <div className="font-display text-2xl font-normal leading-none text-[#0b1f3a]">{value}</div>
      <div className="mt-2 text-sm font-medium text-slate-700">{label}</div>
    </div>
  );
};

const EmptyState = ({ title, copy, icon: Icon = PiSignatureLight, action }) => (
  <div className="flex min-h-[300px] flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 bg-white px-6 py-10 text-center">
    <div className="mb-4 flex items-center justify-center text-sky-600">
      <Icon size={26} />
    </div>
    <h2 className="font-display text-xl font-normal text-[#0b1f3a]">{title}</h2>
    <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">{copy}</p>
    {action}
  </div>
);

const CandidateIdentity = ({ offer }) => {
  const name = candidateName(offer);
  return (
    <div className="flex min-w-0 items-start gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#eef9ff] text-sm font-medium text-sky-700">
        {initialsFor(name)}
      </div>
      <div className="min-w-0">
        <div className="truncate font-display text-[16px] font-normal leading-tight text-[#0b1f3a]">{name}</div>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
          {offer?.candidate?.email && <span className="inline-flex items-center gap-1"><PiEnvelopeSimpleLight size={12} /> {offer.candidate.email}</span>}
          {offer?.candidate?.matchScore !== null && offer?.candidate?.matchScore !== undefined && (
            <span>{Math.round(Number(offer.candidate.matchScore) || 0)}% match</span>
          )}
        </div>
      </div>
    </div>
  );
};

const InfoTile = ({ label, value, detail }) => (
  <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
    <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">{label}</div>
    <div className="mt-1 break-words text-sm font-medium text-[#0b1f3a]">{value}</div>
    {detail && <div className="mt-1 text-xs text-slate-500">{detail}</div>}
  </div>
);

const OfferActions = ({ offer, onDetails, onWithdraw, onNegotiate }) => {
  const canWithdraw = OPEN_STATUSES.includes(offer.status);
  const canRespond = NEGOTIATION_STATUSES.includes(offer.status) || Boolean(offer.pendingNegotiationId);

  return (
    <div className="flex flex-wrap justify-end gap-1.5">
      <button
        onClick={() => onDetails(offer)}
        className="inline-flex h-8 items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-600 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700"
      >
        <PiEyeLight size={12} /> Details
      </button>
      <button
        onClick={() => onNegotiate(offer)}
        disabled={!canRespond}
        className={
          canRespond
            ? "inline-flex h-8 items-center justify-center gap-1.5 rounded-md bg-amber-500 px-2.5 text-xs font-medium text-white transition hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-40 btn-premium-base btn-premium-highlight-amber"
            : "inline-flex h-8 items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-600 transition hover:border-amber-200 hover:bg-amber-50 hover:text-amber-700 disabled:cursor-not-allowed disabled:opacity-40"
        }
      >
        <PiChatCircleLight size={12} /> Respond
      </button>
      <button
        onClick={() => onWithdraw(offer)}
        disabled={!canWithdraw}
        className="inline-flex h-8 items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-600 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <PiArrowUUpLeftLight size={12} /> Withdraw
      </button>
    </div>
  );
};

const OfferTable = ({ offers, onDetails, onWithdraw, onNegotiate }) => (
  <div className="overflow-hidden rounded-lg border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
    <div className="hidden overflow-x-auto lg:block">
      <table className="w-full min-w-[1100px] text-left">
        <thead className="border-b border-slate-100 bg-slate-50/80">
          <tr>
            <th className="px-4 py-3 text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Candidate</th>
            <th className="px-4 py-3 text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Offer</th>
            <th className="px-4 py-3 text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Compensation</th>
            <th className="px-4 py-3 text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Status</th>
            <th className="px-4 py-3 text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Timeline</th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Actions</th>
          </tr>
        </thead>
        <tbody>
          {offers.map((offer) => {
            const due = daysUntil(offer.expiresAt);
            return (
              <tr key={offer.id} className="border-b border-slate-100 last:border-0">
                <td className="px-4 py-4">
                  <CandidateIdentity offer={offer} />
                </td>
                <td className="px-4 py-4">
                  <div className="max-w-[260px]">
                    <div className="truncate text-sm font-medium text-[#0b1f3a]">{offer.title || 'Offer'}</div>
                    <div className="mt-1 truncate text-xs text-slate-500">{offer.job?.title || 'Untitled role'} · {labelize(offer.employmentType)}</div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="text-sm font-medium text-[#0b1f3a]">{formatSalary(offer)}</div>
                  <div className="mt-1 text-xs text-slate-500">Start {formatDate(offer.startDate)}</div>
                </td>
                <td className="px-4 py-4">
                  <StatusPill status={offer.status} />
                  {offer.signatureRequired && (
                    <div className="mt-2 inline-flex items-center gap-1 text-xs text-slate-500">
                      <PiShieldCheckLight size={12} /> Signature required
                    </div>
                  )}
                </td>
                <td className="px-4 py-4">
                  <div className="text-sm text-slate-600">Sent {formatDate(offer.createdAt)}</div>
                  <div className={`mt-1 text-xs ${due !== null && due <= 2 && OPEN_STATUSES.includes(offer.status) ? 'text-rose-600' : 'text-slate-500'}`}>
                    Expires {formatDate(offer.expiresAt)}{due !== null && OPEN_STATUSES.includes(offer.status) ? ` · ${due < 0 ? 'overdue' : `${due}d`}` : ''}
                  </div>
                </td>
                <td className="px-4 py-4 text-right">
                  <OfferActions offer={offer} onDetails={onDetails} onWithdraw={onWithdraw} onNegotiate={onNegotiate} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>

    <div className="divide-y divide-slate-100 lg:hidden">
      {offers.map((offer) => (
        <article key={offer.id} className="p-4">
          <div className="flex items-start justify-between gap-3">
            <CandidateIdentity offer={offer} />
            <StatusPill status={offer.status} />
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <InfoTile label="Role" value={offer.job?.title || 'Untitled role'} detail={offer.title || 'Offer'} />
            <InfoTile label="Compensation" value={formatSalary(offer)} detail={`Start ${formatDate(offer.startDate)}`} />
            <InfoTile label="Expiry" value={formatDate(offer.expiresAt)} detail={offer.negotiable ? 'Negotiable' : 'Fixed terms'} />
            <InfoTile label="Documents" value={`${getDocuments(offer).length} attached`} detail={offer.signatureRequired ? 'Signature required' : 'Signature optional'} />
          </div>
          <div className="mt-4">
            <OfferActions offer={offer} onDetails={onDetails} onWithdraw={onWithdraw} onNegotiate={onNegotiate} />
          </div>
        </article>
      ))}
    </div>
  </div>
);

const BoardColumn = ({ title, offers, onDetails, onWithdraw, onNegotiate }) => (
  <section className="min-w-[290px] flex-1">
    <div className="mb-3 flex items-center justify-between px-1">
      <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{title}</h2>
      <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">{offers.length}</span>
    </div>
    <div className="space-y-3">
      {offers.length === 0 ? (
        <div className="flex h-24 items-center justify-center rounded-lg border border-dashed border-slate-200 bg-white text-sm text-slate-400">
          No offers
        </div>
      ) : offers.map((offer) => (
        <article key={offer.id} className="rounded-lg border border-slate-200 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
          <div className="mb-3 flex items-start justify-between gap-3">
            <CandidateIdentity offer={offer} />
            <StatusPill status={offer.status} />
          </div>
          <div className="space-y-2 text-sm">
            <div>
              <div className="font-medium text-[#0b1f3a]">{offer.title || 'Offer'}</div>
              <div className="mt-1 text-xs text-slate-500">{offer.job?.title || 'Untitled role'}</div>
            </div>
            <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
              <div className="font-medium text-[#0b1f3a]">{formatSalary(offer)}</div>
              <div className="mt-1 text-xs text-slate-500">
                Expires {formatDate(offer.expiresAt)} · {offer.negotiable ? 'Negotiable' : 'Fixed'}
              </div>
            </div>
          </div>
          <div className="mt-4">
            <OfferActions offer={offer} onDetails={onDetails} onWithdraw={onWithdraw} onNegotiate={onNegotiate} />
          </div>
        </article>
      ))}
    </div>
  </section>
);

const OfferBoard = ({ offers, onDetails, onWithdraw, onNegotiate }) => {
  const groups = [
    { title: 'Active', items: offers.filter((offer) => ['sent', 'viewed'].includes(offer.status)) },
    { title: 'Negotiation', items: offers.filter((offer) => NEGOTIATION_STATUSES.includes(offer.status)) },
    { title: 'Accepted', items: offers.filter((offer) => offer.status === 'accepted') },
    { title: 'Declined', items: offers.filter((offer) => offer.status === 'rejected') },
    { title: 'Closed', items: offers.filter((offer) => ['withdrawn', 'expired'].includes(offer.status)) }
  ];

  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {groups.map((group) => (
        <BoardColumn
          key={group.title}
          title={group.title}
          offers={group.items}
          onDetails={onDetails}
          onWithdraw={onWithdraw}
          onNegotiate={onNegotiate}
        />
      ))}
    </div>
  );
};

const DocumentRow = ({ document }) => {
  const href = resolveUploadUrl(document.path || document.metadata?.path);
  const name = document.metadata?.originalName || document.metadata?.filename || document.path || 'Document';

  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
      <div className="min-w-0">
        <div className="text-sm font-medium text-[#0b1f3a]">{document.label}</div>
        <div className="mt-1 truncate text-xs text-slate-500">{name}</div>
      </div>
      {href && (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700"
          title="Open document"
        >
          <PiDownloadSimpleLight size={12} />
        </a>
      )}
    </div>
  );
};

const DetailModal = ({ open, loading, detail, fallbackOffer, onClose, onWithdraw, onNegotiate }) => {
  if (!open) return null;

  const offer = detail?.offer || fallbackOffer || {};
  const documents = getDocuments(offer);
  const negotiations = Array.isArray(detail?.negotiations) ? detail.negotiations : [];
  const pendingNegotiation = latestPendingNegotiation(negotiations);
  const canWithdraw = OPEN_STATUSES.includes(offer.status);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/40 px-4 py-6 backdrop-blur-sm">
      <div className="w-full max-w-6xl rounded-lg border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.22)]">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-4 py-4 sm:px-5">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-display text-xl font-normal text-[#0b1f3a]">{offer.title || 'Offer details'}</h2>
              <StatusPill status={offer.status} />
            </div>
            <p className="mt-1 text-sm text-slate-500">{candidateName(offer)} · {offer.job?.title || 'Untitled role'}</p>
          </div>
          <button onClick={onClose} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50" title="Close">
            <PiXLight size={13} />
          </button>
        </div>

        {loading ? (
          <div className="flex min-h-[360px] items-center justify-center text-sm text-slate-500">
            <PiCircleNotchLight className="mr-2 animate-spin" size={14} /> Loading offer details
          </div>
        ) : (
          <div className="grid gap-5 p-4 sm:p-5 lg:grid-cols-[1.2fr_0.8fr]">
            <div className="space-y-5">
              <section>
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Commercial terms</h3>
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  <InfoTile label="Compensation" value={formatSalary(offer)} detail={labelize(offer.employmentType)} />
                  <InfoTile label="Start date" value={formatDate(offer.startDate)} detail={`Expires ${formatDate(offer.expiresAt)}`} />
                  <InfoTile label="Negotiation" value={offer.negotiable ? 'Allowed' : 'Fixed'} detail={offer.negotiationStatus ? labelize(offer.negotiationStatus) : 'No active counter'} />
                  <InfoTile label="Signature" value={offer.signatureRequired ? 'Required' : 'Optional'} detail={offer.signedAt ? `Signed ${formatDate(offer.signedAt)}` : 'No signature recorded'} />
                  <InfoTile label="Candidate response" value={offer.candidateResponseMessage || 'No response message'} detail={offer.respondedAt ? formatDateTime(offer.respondedAt) : 'Awaiting response'} />
                  <InfoTile label="Employer response" value={offer.employerResponseMessage || 'No response message'} detail={offer.lastNegotiationAt ? formatDateTime(offer.lastNegotiationAt) : 'No negotiation response'} />
                </div>
              </section>

              <section className="grid gap-3 lg:grid-cols-2">
                <div>
                  <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Benefits</h3>
                  <pre className="min-h-[150px] whitespace-pre-wrap rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm leading-6 text-slate-700">{formatPayload(offer.benefits)}</pre>
                </div>
                <div>
                  <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Terms snapshot</h3>
                  <pre className="min-h-[150px] whitespace-pre-wrap rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm leading-6 text-slate-700">{formatPayload(offer.termsSnapshot)}</pre>
                </div>
              </section>

              <section>
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Message and signing instructions</h3>
                <div className="grid gap-3 lg:grid-cols-2">
                  <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm leading-6 text-slate-700">
                    {offer.customMessage || 'No custom message included.'}
                  </div>
                  <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm leading-6 text-slate-700">
                    {offer.signingInstructions || 'No signing instructions included.'}
                  </div>
                </div>
              </section>
            </div>

            <aside className="space-y-5">
              <section>
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Candidate</h3>
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <CandidateIdentity offer={offer} />
                  <div className="mt-4 grid gap-2">
                    <InfoTile label="Role" value={offer.job?.title || 'Untitled role'} detail={`Match ${offer.matchId || 'not shown'}`} />
                    <InfoTile label="Created" value={formatDateTime(offer.createdAt)} detail={`Updated ${formatDateTime(offer.updatedAt)}`} />
                  </div>
                </div>
              </section>

              <section>
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Documents</h3>
                <div className="space-y-2">
                  {documents.length > 0 ? documents.map((document, index) => (
                    <DocumentRow key={`${document.label}-${index}`} document={document} />
                  )) : (
                    <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
                      No documents attached
                    </div>
                  )}
                </div>
              </section>

              <section>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Negotiations</h3>
                  {pendingNegotiation && (
                    <button
                      onClick={() => onNegotiate({ ...offer, pendingNegotiationId: pendingNegotiation.id }, pendingNegotiation)}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md bg-amber-500 px-3 text-xs font-medium text-white transition hover:bg-amber-600 btn-premium-base btn-premium-highlight-amber"
                    >
                      <PiChatCircleLight size={12} /> Respond
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {negotiations.length > 0 ? negotiations.map((item) => (
                    <div key={item.id} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-medium text-[#0b1f3a]">{labelize(item.sender?.role)} proposal</div>
                          <div className="mt-1 text-xs text-slate-500">{formatDateTime(item.createdAt)}</div>
                        </div>
                        <span className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600">{labelize(item.status)}</span>
                      </div>
                      {item.message && <p className="mt-3 text-sm leading-6 text-slate-600">{item.message}</p>}
                      <div className="mt-3 grid gap-2 text-xs text-slate-600">
                        {item.proposedSalaryAmount && <span>{formatCurrency(item.proposedSalaryAmount, item.proposedSalaryCurrency)} / {salaryPeriods[item.proposedSalaryPeriod] || item.proposedSalaryPeriod}</span>}
                        {item.proposedStartDate && <span>Start {formatDate(item.proposedStartDate)}</span>}
                        {item.employerResponseMessage && <span>Employer response: {item.employerResponseMessage}</span>}
                      </div>
                    </div>
                  )) : (
                    <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
                      No negotiation history
                    </div>
                  )}
                </div>
              </section>

              <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
                <button onClick={onClose} className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50">
                  Close
                </button>
                <button
                  onClick={() => onWithdraw(offer)}
                  disabled={!canWithdraw}
                  className="inline-flex h-9 items-center gap-2 rounded-lg border border-rose-100 bg-rose-50 px-3 text-sm font-medium text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-45"
                >
                  <PiArrowUUpLeftLight size={12} /> Withdraw
                </button>
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
};

const WithdrawModal = ({ offer, busy, onClose, onSubmit }) => {
  const [reason, setReason] = useState('');
  const [notifyCandidate, setNotifyCandidate] = useState(true);

  useEffect(() => {
    setReason('');
    setNotifyCandidate(true);
  }, [offer?.id]);

  if (!offer) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4 py-6 backdrop-blur-sm">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit({ reason: reason.trim(), notifyCandidate });
        }}
        className="w-full max-w-lg rounded-lg border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.22)]"
      >
        <div className="border-b border-slate-100 px-5 py-4">
          <h2 className="font-display text-xl font-normal text-[#0b1f3a]">Withdraw offer</h2>
          <p className="mt-1 text-sm text-slate-500">{candidateName(offer)} · {offer.job?.title || offer.title}</p>
        </div>
        <div className="space-y-4 p-5">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Reason</span>
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={5}
              maxLength={4000}
              className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              placeholder="Record the business reason for withdrawing this offer."
              required
            />
          </label>
          <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={notifyCandidate}
              onChange={(event) => setNotifyCandidate(event.target.checked)}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
            />
            <span>Notify the candidate and send the withdrawal email.</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button type="button" onClick={onClose} className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50">
            Cancel
          </button>
          <button type="submit" disabled={busy || !reason.trim()} className="inline-flex h-9 items-center gap-2 rounded-lg bg-rose-600 px-3 text-sm font-medium text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60">
            {busy ? <PiCircleNotchLight className="animate-spin" size={12} /> : <PiArrowUUpLeftLight size={12} />}
            Withdraw
          </button>
        </div>
      </form>
    </div>
  );
};

const NegotiationModal = ({ offer, negotiation, busy, onClose, onSubmit }) => {
  const [action, setAction] = useState('accept');
  const [form, setForm] = useState({
    message: '',
    proposedSalaryAmount: '',
    proposedSalaryCurrency: USD_CURRENCY,
    proposedSalaryPeriod: offer?.salaryPeriod || 'monthly',
    proposedStartDate: offer?.startDate ? String(offer.startDate).slice(0, 10) : '',
    proposedBenefits: formatPayload(offer?.benefits) === 'Not provided' ? '' : formatPayload(offer?.benefits),
    proposedTerms: formatPayload(offer?.termsSnapshot) === 'Not provided' ? '' : formatPayload(offer?.termsSnapshot)
  });

  useEffect(() => {
    if (!offer) return;
    setAction('accept');
    setForm({
      message: '',
      proposedSalaryAmount: '',
      proposedSalaryCurrency: USD_CURRENCY,
      proposedSalaryPeriod: offer?.salaryPeriod || 'monthly',
      proposedStartDate: offer?.startDate ? String(offer.startDate).slice(0, 10) : '',
      proposedBenefits: formatPayload(offer?.benefits) === 'Not provided' ? '' : formatPayload(offer?.benefits),
      proposedTerms: formatPayload(offer?.termsSnapshot) === 'Not provided' ? '' : formatPayload(offer?.termsSnapshot)
    });
  }, [offer, negotiation?.id]);

  if (!offer || !negotiation) return null;

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/40 px-4 py-6 backdrop-blur-sm">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const payload = { action, message: form.message.trim() };
          if (action === 'counter') {
            if (form.proposedSalaryAmount) payload.proposedSalaryAmount = Number(form.proposedSalaryAmount);
            if (form.proposedSalaryAmount) payload.proposedSalaryCurrency = USD_CURRENCY;
            if (form.proposedSalaryPeriod) payload.proposedSalaryPeriod = form.proposedSalaryPeriod;
            if (form.proposedStartDate) payload.proposedStartDate = form.proposedStartDate;
            if (form.proposedBenefits.trim()) payload.proposedBenefits = form.proposedBenefits.trim();
            if (form.proposedTerms.trim()) payload.proposedTerms = form.proposedTerms.trim();
          }
          onSubmit(payload);
        }}
        className="w-full max-w-3xl rounded-lg border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.22)]"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
          <div>
            <h2 className="font-display text-xl font-normal text-[#0b1f3a]">Respond to negotiation</h2>
            <p className="mt-1 text-sm text-slate-500">{candidateName(offer)} · {offer.job?.title || offer.title}</p>
          </div>
          <button type="button" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50" title="Close">
            <PiXLight size={13} />
          </button>
        </div>

        <div className="space-y-5 p-5">
          <div className="rounded-lg border border-amber-100 bg-amber-50 p-3">
            <div className="text-sm font-medium text-amber-800">Candidate proposal</div>
            <div className="mt-2 grid gap-2 text-sm text-amber-900 sm:grid-cols-2">
              <span>{negotiation.proposedSalaryAmount ? formatCurrency(negotiation.proposedSalaryAmount, negotiation.proposedSalaryCurrency) : 'No salary change'}</span>
              <span>{negotiation.proposedStartDate ? `Start ${formatDate(negotiation.proposedStartDate)}` : 'No start date change'}</span>
            </div>
            {negotiation.message && <p className="mt-3 text-sm leading-6 text-amber-900">{negotiation.message}</p>}
          </div>

          <div className="grid gap-2 sm:grid-cols-3">
            {[
              { value: 'accept', label: 'Accept', icon: PiCheckCircleLight },
              { value: 'reject', label: 'Reject', icon: PiXCircleLight },
              { value: 'counter', label: 'Counter', icon: PiChatCircleLight }
            ].map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => setAction(item.value)}
                className={`flex h-10 items-center justify-center gap-2 rounded-lg border px-3 text-sm font-medium transition ${
                  action === item.value
                    ? 'border-[#0b1f3a] bg-[#0b1f3a] text-white'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                <item.icon size={12} /> {item.label}
              </button>
            ))}
          </div>

          <label className="block">
            <span className="text-sm font-medium text-slate-700">Message</span>
            <textarea
              value={form.message}
              onChange={(event) => update('message', event.target.value)}
              rows={4}
              maxLength={4000}
              className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              placeholder="Add the response the candidate should see."
            />
          </label>

          {action === 'counter' && (
            <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="grid gap-3 sm:grid-cols-3">
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">Salary amount</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.proposedSalaryAmount}
                    onChange={(event) => update('proposedSalaryAmount', event.target.value)}
                    className="mt-2 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                    placeholder={String(offer.salaryAmount || '')}
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">Currency</span>
                  <input
                    value={USD_CURRENCY}
                    readOnly
                    className="mt-2 h-10 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm uppercase outline-none"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">Period</span>
                  <select
                    value={form.proposedSalaryPeriod}
                    onChange={(event) => update('proposedSalaryPeriod', event.target.value)}
                    className="mt-2 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                  >
                    {Object.keys(salaryPeriods).map((period) => <option key={period} value={period}>{labelize(period)}</option>)}
                  </select>
                </label>
              </div>
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Start date</span>
                <input
                  type="date"
                  value={form.proposedStartDate}
                  onChange={(event) => update('proposedStartDate', event.target.value)}
                  className="mt-2 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                />
              </label>
              <div className="grid gap-3 lg:grid-cols-2">
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">Benefits</span>
                  <textarea
                    rows={5}
                    value={form.proposedBenefits}
                    onChange={(event) => update('proposedBenefits', event.target.value)}
                    className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">Terms</span>
                  <textarea
                    rows={5}
                    value={form.proposedTerms}
                    onChange={(event) => update('proposedTerms', event.target.value)}
                    className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                  />
                </label>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button type="button" onClick={onClose} className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50">
            Cancel
          </button>
          <button type="submit" disabled={busy} className="inline-flex h-9 items-center gap-2 rounded-lg bg-[#0b1f3a] px-3 text-sm font-medium text-white transition hover:bg-[#12345c] disabled:cursor-not-allowed disabled:opacity-60">
            {busy ? <PiCircleNotchLight className="animate-spin" size={12} /> : <PiCheckCircleLight size={12} />}
            Save response
          </button>
        </div>
      </form>
    </div>
  );
};

const OffersOverview = () => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('table');
  const [showFilters, setShowFilters] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [fallbackOffer, setFallbackOffer] = useState(null);
  const [withdrawOffer, setWithdrawOffer] = useState(null);
  const [negotiationState, setNegotiationState] = useState({ offer: null, negotiation: null });
  const [actionBusy, setActionBusy] = useState(false);
  const [isHighlighting, setIsHighlighting] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsHighlighting(false), 3500);
    return () => clearTimeout(timer);
  }, []);

  const loadOffers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const limit = 100;
      let offset = 0;
      let hasMore = false;
      const loadedOffers = [];

      do {
        const response = await offerAPI.getEmployerOffers({ limit, offset });
        const pageOffers = Array.isArray(response.offers) ? response.offers : [];
        loadedOffers.push(...pageOffers);
        hasMore = Boolean(response.pagination?.hasMore);
        offset += limit;
      } while (hasMore && loadedOffers.length < 500);

      setOffers(loadedOffers);
      setPagination({ limit, offset: 0, hasMore });
    } catch (loadError) {
      setError(extractError(loadError, 'Failed to load offers'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOffers();
  }, [loadOffers]);

  const filteredOffers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return offers;

    return offers.filter((offer) => {
      if (statusFilter !== 'all' && offer.status !== statusFilter) return false;
      return [
      offer.title,
      offer.status,
      offer.job?.title,
      candidateName(offer),
      offer.candidate?.email,
      offer.employmentType,
      offer.salaryCurrency
    ].filter(Boolean).join(' ').toLowerCase().includes(query);
    });
  }, [offers, search, statusFilter]);

  const metrics = useMemo(() => {
    const total = offers.length;
    const open = offers.filter((offer) => OPEN_STATUSES.includes(offer.status)).length;
    const accepted = offers.filter((offer) => offer.status === 'accepted').length;
    const negotiation = offers.filter((offer) => NEGOTIATION_STATUSES.includes(offer.status)).length;
    const signedRequired = offers.filter((offer) => offer.signatureRequired).length;
    const salaryRows = offers.filter((offer) => Number(offer.salaryAmount) > 0);
    const currencies = new Set(salaryRows.map((offer) => String(offer.salaryCurrency || 'USD').toUpperCase()));
    const avgSalary = salaryRows.length
      ? salaryRows.reduce((sum, offer) => sum + Number(offer.salaryAmount), 0) / salaryRows.length
      : 0;
    const salaryCurrency = currencies.size === 1 ? Array.from(currencies)[0] : null;
    const expiringSoon = offers.filter((offer) => {
      const due = daysUntil(offer.expiresAt);
      return OPEN_STATUSES.includes(offer.status) && due !== null && due >= 0 && due <= 3;
    }).length;
    const acceptanceRate = total ? Math.round((accepted / total) * 100) : 0;

    return { total, open, accepted, negotiation, signedRequired, avgSalary, salaryCurrency, expiringSoon, acceptanceRate };
  }, [offers]);

  const openDetails = useCallback(async (offer) => {
    setFallbackOffer(offer);
    setDetail(null);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const response = await offerAPI.getDetails(offer.id);
      setDetail(response);
    } catch (detailError) {
      toast.error(extractError(detailError, 'Failed to load offer details'));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const openNegotiation = useCallback(async (offer, knownNegotiation = null) => {
    if (knownNegotiation) {
      setNegotiationState({ offer, negotiation: knownNegotiation });
      return;
    }

    setActionBusy(true);
    try {
      const response = await offerAPI.getDetails(offer.id);
      const pending = latestPendingNegotiation(response.negotiations);
      if (!pending) {
        toast.error('No pending candidate negotiation found for this offer.');
        return;
      }
      setNegotiationState({ offer: response.offer || offer, negotiation: pending });
    } catch (negotiationError) {
      toast.error(extractError(negotiationError, 'Failed to load negotiation'));
    } finally {
      setActionBusy(false);
    }
  }, []);

  const submitWithdraw = async (payload) => {
    if (!withdrawOffer || !payload.reason) return;

    setActionBusy(true);
    try {
      await offerAPI.withdraw(withdrawOffer.id, payload);
      toast.success('Offer withdrawn');
      setWithdrawOffer(null);
      if (detailOpen) {
        const response = await offerAPI.getDetails(withdrawOffer.id);
        setDetail(response);
      }
      await loadOffers();
    } catch (withdrawError) {
      toast.error(extractError(withdrawError, 'Failed to withdraw offer'));
    } finally {
      setActionBusy(false);
    }
  };

  const submitNegotiation = async (payload) => {
    const { offer, negotiation } = negotiationState;
    if (!offer || !negotiation) return;

    setActionBusy(true);
    try {
      await offerAPI.respondToNegotiation(offer.id, negotiation.id, payload);
      toast.success('Negotiation response saved');
      setNegotiationState({ offer: null, negotiation: null });
      if (detailOpen) {
        const response = await offerAPI.getDetails(offer.id);
        setDetail(response);
      }
      await loadOffers();
    } catch (negotiationError) {
      toast.error(extractError(negotiationError, 'Failed to respond to negotiation'));
    } finally {
      setActionBusy(false);
    }
  };

  if (loading && offers.length === 0) {
    return <OffersOverviewSkeleton />;
  }

  return (
    <div className="min-h-screen bg-white px-4 py-6 font-sans sm:px-6 lg:px-8">
      <style>{`
        @keyframes shimmer-sweep {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
        @keyframes subtle-pulse-ring {
          0% { box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.25); }
          50% { box-shadow: 0 0 0 3px rgba(0, 0, 0, 0); }
          100% { box-shadow: 0 0 0 0 rgba(0, 0, 0, 0); }
        }
        @keyframes subtle-pulse-ring-emerald {
          0% { box-shadow: 0 0 0 0 rgba(5, 150, 105, 0.4); }
          50% { box-shadow: 0 0 0 4px rgba(5, 150, 105, 0); }
          100% { box-shadow: 0 0 0 0 rgba(5, 150, 105, 0); }
        }
        @keyframes subtle-pulse-ring-sky {
          0% { box-shadow: 0 0 0 0 rgba(2, 132, 199, 0.4); }
          50% { box-shadow: 0 0 0 4px rgba(2, 132, 199, 0); }
          100% { box-shadow: 0 0 0 0 rgba(2, 132, 199, 0); }
        }
        @keyframes subtle-pulse-ring-amber {
          0% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.4); }
          50% { box-shadow: 0 0 0 4px rgba(245, 158, 11, 0); }
          100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
        }
        .btn-premium-base {
          position: relative;
          overflow: hidden;
        }
        .btn-premium-base::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(110deg, transparent 25%, rgba(255, 255, 255, 0.4) 50%, transparent 75%);
          background-size: 200% 100%;
          animation: shimmer-sweep 2s infinite linear;
          opacity: 0;
          transition: opacity 1s ease-in-out;
          pointer-events: none;
        }
        .btn-premium-highlight::after {
          opacity: 1;
        }
        .btn-premium-highlight {
          animation: subtle-pulse-ring 2s infinite cubic-bezier(0.4, 0, 0.2, 1);
        }
        .btn-premium-highlight-emerald {
          animation: subtle-pulse-ring-emerald 2s infinite cubic-bezier(0.4, 0, 0.2, 1);
        }
        .btn-premium-highlight-sky {
          animation: subtle-pulse-ring-sky 2s infinite cubic-bezier(0.4, 0, 0.2, 1);
        }
        .btn-premium-highlight-amber {
          animation: subtle-pulse-ring-amber 2s infinite cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
      <div className="mx-auto max-w-[1600px] space-y-6">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.16em] text-sky-700">Offers</p>
            <h1 className="mt-2 font-display text-3xl font-normal tracking-tight text-[#0b1f3a]">Offer Management</h1>
            <p className="mt-2 text-sm leading-6 text-slate-500">Send, negotiate, track, and close candidate offers from eligible matches.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setViewMode((mode) => mode === 'table' ? 'board' : 'table')}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 shadow-[0_8px_20px_rgba(15,23,42,0.035)] transition hover:bg-slate-50"
            >
              {viewMode === 'table' ? <PiColumnsLight size={13} /> : <PiSlidersHorizontalLight size={13} />}
              {viewMode === 'table' ? 'Board' : 'Table'}
            </button>
            <button
              onClick={() => navigate('/offers/create')}
              className={`inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-lg px-4 text-sm font-medium text-white shadow-[0_12px_28px_rgba(11,31,58,0.18)] btn-premium-base transition-colors duration-1000 ${
                isHighlighting
                  ? 'bg-black btn-premium-highlight'
                  : 'bg-[#0b1f3a] hover:bg-[#12345c]'
              }`}
            >
              <PiPlusLight size={13} /> New offer
            </button>
          </div>
        </header>

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Metric icon={PiSignatureLight} label="Total offers" value={formatNumber(metrics.total)} detail={`${metrics.open} active`} tone="sky" />
          <Metric icon={PiCheckCircleLight} label="Acceptance rate" value={`${metrics.acceptanceRate}%`} detail={`${metrics.accepted} accepted`} tone="green" />
          <Metric icon={PiCurrencyDollarLight} label="Average salary" value={metrics.avgSalary ? (metrics.salaryCurrency ? formatCurrency(metrics.avgSalary, metrics.salaryCurrency) : 'Mixed currencies') : 'No salary'} detail="Listed offers" tone="slate" />
          <Metric icon={PiWarningCircleLight} label="Needs attention" value={formatNumber(metrics.negotiation + metrics.expiringSoon)} detail={`${metrics.signedRequired} require signature`} tone="amber" />
        </section>

        <section className="rounded-lg border border-slate-200/80 bg-white p-3 shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="relative min-w-0 flex-1">
              <PiMagnifyingGlassLight className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                placeholder="Search by candidate, role, status, or email"
              />
            </div>
            <button
              onClick={() => setShowFilters((value) => !value)}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50 lg:hidden"
            >
              <PiFunnelLight size={13} /> Filters
            </button>
            <div className={`${showFilters ? 'grid' : 'hidden'} gap-2 sm:grid-cols-2 lg:flex`}>
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              >
                {statusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </div>
          </div>
        </section>

        {error && (
          <div className="flex items-start gap-3 rounded-lg border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <PiWarningCircleLight className="mt-0.5 shrink-0" size={14} />
            <div className="min-w-0">
              <div className="font-medium">Offers could not be loaded</div>
              <div className="mt-1">{error}</div>
            </div>
          </div>
        )}

        {filteredOffers.length === 0 ? (
          <EmptyState
            title="No offers found"
            copy={offers.length === 0 ? 'Create offers from mutual, interview-passed, or assessment-passed candidate matches.' : 'No offers match the current search and status filters.'}
            action={(
              <button
                onClick={() => navigate('/offers/create')}
                className={`mt-5 inline-flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-medium text-white btn-premium-base transition-colors duration-1000 ${
                  isHighlighting
                    ? 'bg-black btn-premium-highlight'
                    : 'bg-[#0b1f3a] hover:bg-[#12345c]'
                }`}
              >
                <PiPlusLight size={13} /> New offer
              </button>
            )}
          />
        ) : viewMode === 'table' ? (
          <OfferTable offers={filteredOffers} onDetails={openDetails} onWithdraw={setWithdrawOffer} onNegotiate={openNegotiation} />
        ) : (
          <OfferBoard offers={filteredOffers} onDetails={openDetails} onWithdraw={setWithdrawOffer} onNegotiate={openNegotiation} />
        )}

        {pagination?.hasMore && (
          <div className="rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Showing the first {pagination.limit} offers. Use a status filter to narrow the list.
          </div>
        )}
      </div>

      <DetailModal
        open={detailOpen}
        loading={detailLoading}
        detail={detail}
        fallbackOffer={fallbackOffer}
        onClose={() => setDetailOpen(false)}
        onWithdraw={setWithdrawOffer}
        onNegotiate={openNegotiation}
      />
      <WithdrawModal
        offer={withdrawOffer}
        busy={actionBusy}
        onClose={() => setWithdrawOffer(null)}
        onSubmit={submitWithdraw}
      />
      <NegotiationModal
        offer={negotiationState.offer}
        negotiation={negotiationState.negotiation}
        busy={actionBusy}
        onClose={() => setNegotiationState({ offer: null, negotiation: null })}
        onSubmit={submitNegotiation}
      />
    </div>
  );
};

export default OffersOverview;
